import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  deletePasskey,
  isPasskeySupported,
  listPasskeys,
  registerPasskey,
  type PasskeyRow,
} from '../lib/passkey';

/**
 * Lists the user's passkeys with add/delete. Defaults the device-name
 * field to a sensible per-platform guess so most users don't have to
 * touch the input.
 */
export function PasskeyModal({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation(['passkey', 'common']);
  const [items, setItems] = useState<PasskeyRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [deviceName, setDeviceName] = useState(suggestedDeviceName());
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    try {
      const list = await listPasskeys();
      setItems(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function add() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await registerPasskey(deviceName.trim() || undefined);
      setMsg(t('registered'));
      await load();
    } catch (e) {
      const m = (e as Error)?.message ?? '';
      if (/cancel|abort|NotAllowed/i.test(m)) {
        // user-cancelled, silent
      } else {
        setErr(m || t('errors.failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: PasskeyRow) {
    if (!confirm(t('delete_confirm', { name: row.deviceName ?? row.credentialId.slice(0, 8) }))) {
      return;
    }
    setBusy(true);
    try {
      await deletePasskey(row.id);
      setMsg(t('removed'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const supported = isPasskeySupported();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">🔑 {t('title')}</div>
            <div className="form-preview-type">{t('subtitle')}</div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        {!supported && (
          <div className="form-section" style={{ color: 'var(--text2)' }}>
            {t('unsupported')}
          </div>
        )}

        {supported && (
          <div className="form-section">
            <div className="section-label">{t('add_section')}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
              {t('add_hint')}
            </div>
            <div className="field">
              <label>{t('device_name')}</label>
              <input
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                disabled={busy}
                placeholder={t('device_name_placeholder')}
              />
            </div>
            <div className="form-actions" style={{ marginTop: 10 }}>
              <button className="btn-primary" onClick={add} disabled={busy}>
                {busy ? t('common:actions.loading_dots') : t('add_button')}
              </button>
            </div>
          </div>
        )}

        <div className="form-section">
          <div className="section-label">{t('list_section')}</div>
          {!items ? (
            <div className="empty">{t('common:actions.loading')}</div>
          ) : items.length === 0 ? (
            <div className="empty">{t('list_empty')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {p.deviceName ?? t('unnamed_device')}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {p.lastUsedAt
                        ? t('last_used', {
                            when: new Date(p.lastUsedAt).toLocaleDateString(i18n.language, {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }),
                          })
                        : t('never_used')}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => remove(p)}
                    disabled={busy}
                    style={{ color: 'var(--red)' }}
                  >
                    {t('delete')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {msg && (
          <div className="form-section" style={{ color: 'var(--green)' }}>
            {msg}
          </div>
        )}
        {err && (
          <div className="form-section" style={{ color: 'var(--red)' }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

function suggestedDeviceName(): string {
  if (typeof navigator === 'undefined') return 'Passkey';
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Passkey';
}
